import type { AlterationResult } from '../services/types';

/**
 * Extract term total premium from Entsia premiums array (POLSUM with premiumType Term when present).
 * Matches api getPolicyPremiumFromPremiums so portal compares like-for-like with policy.premium.
 */
export function getDisplayPremiumFromAlteration(alteration: AlterationResult): number | null {
  if (!alteration.premiums?.length) return null;
  let fallbackPolsum: number | null = null;
  for (const p of alteration.premiums) {
    const item = p as Record<string, unknown>;
    const premium = item?.premium as Record<string, unknown> | undefined;
    const level = (premium?.level ?? '') as string;
    if (level !== 'POLSUM') continue;
    const premiumItem = premium?.premiumItem as Record<string, unknown> | undefined;
    const pType = String(premiumItem?.premiumType ?? '').trim();
    const gross = (premiumItem?.grossPremium ?? premium?.grossPremium) as { value?: number } | undefined;
    if (typeof gross?.value !== 'number') continue;
    if (pType === 'Term') return gross.value;
    if (fallbackPolsum === null) fallbackPolsum = gross.value;
  }
  return fallbackPolsum;
}

/**
 * Format premium for display (e.g. "$5,174.36").
 */
export function formatPremiumDisplay(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** @deprecated Prefer getPremiumComparisonLines; term total was wrongly paired with this cadence label. */
export function getPremiumFrequencyLabel(premiumFrequency: string | undefined): string {
  const f = (premiumFrequency ?? '').toLowerCase();
  return f === 'monthly' ? '(monthly)' : '(annually)';
}

/** Label for instalment row from Entsia paymentFrequency (e.g. MONTHLY). */
export function getPeriodPaymentTitle(paymentFrequencyRaw: string | undefined): string | null {
  const freq = String(paymentFrequencyRaw ?? '').toUpperCase();
  if (freq === 'MONTHLY') return 'Monthly payment';
  if (freq === 'QUARTERLY') return 'Quarterly payment';
  return null;
}

/**
 * Estimate new per-period payment when the quote only returns term POLSUM (no billing plan on client).
 * Scales by term premium ratio; returns null if not applicable.
 */
export function estimateNewInstalmentAmount(
  oldTermPremium: number,
  newTermPremium: number,
  oldInstalment: number
): number | null {
  if (oldTermPremium <= 0 || Number.isNaN(oldTermPremium) || Number.isNaN(newTermPremium)) return null;
  if (Number.isNaN(oldInstalment) || oldInstalment < 0) return null;
  const n = oldInstalment * (newTermPremium / oldTermPremium);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

export interface PremiumComparisonLines {
  unchanged: boolean;
  oldTermLine?: string;
  oldPaymentLine?: string;
  newTermLine?: string;
  newPaymentLine?: string;
  diffTermLine?: string;
  diffPaymentLine?: string;
}

/**
 * Premium block for alteration confirm: policy term total (POLSUM) plus monthly/quarterly payment when relevant.
 * Instalment after quote is estimated by scaling the previous instalment by newTerm/oldTerm when Entsia does not send billingPlan to the client.
 */
export function getPremiumComparisonLines(params: {
  oldTermPremium: number | null | undefined;
  newTermPremium: number | null | undefined;
  oldInstalment: number | null | undefined;
  paymentFrequencyRaw: string | undefined;
}): PremiumComparisonLines {
  const { oldTermPremium, newTermPremium, oldInstalment, paymentFrequencyRaw } = params;
  const periodTitle = getPeriodPaymentTitle(paymentFrequencyRaw);

  const oldTerm = oldTermPremium != null && !Number.isNaN(oldTermPremium) ? oldTermPremium : null;
  const newTerm = newTermPremium != null && !Number.isNaN(newTermPremium) ? newTermPremium : null;

  if (oldTerm === null && newTerm === null) {
    return { unchanged: true };
  }

  const oldInst =
    oldInstalment != null && !Number.isNaN(oldInstalment) && oldInstalment >= 0 ? oldInstalment : null;
  const newInst =
    periodTitle && oldInst != null && oldTerm != null && newTerm != null
      ? estimateNewInstalmentAmount(oldTerm, newTerm, oldInst)
      : null;

  const termUnchanged =
    oldTerm !== null && newTerm !== null && Math.abs(oldTerm - newTerm) < 0.005;
  const payUnchanged =
    !periodTitle ||
    oldInst === null ||
    newInst === null ||
    Math.abs(newInst - oldInst) < 0.005;

  if (termUnchanged && payUnchanged) {
    return { unchanged: true };
  }

  const out: PremiumComparisonLines = { unchanged: false };

  if (oldTerm !== null) {
    out.oldTermLine = `Policy premium (full term): ${formatPremiumDisplay(oldTerm)}`;
  }
  if (periodTitle && oldInst !== null) {
    out.oldPaymentLine = `${periodTitle}: ${formatPremiumDisplay(oldInst)}`;
  }

  if (newTerm !== null) {
    out.newTermLine = `Policy premium (full term): ${formatPremiumDisplay(newTerm)}`;
  } else if (oldTerm !== null) {
    out.newTermLine = `Policy premium (full term): —`;
  }

  if (periodTitle) {
    if (newInst !== null) {
      out.newPaymentLine = `${periodTitle}: ${formatPremiumDisplay(newInst)}`;
    } else if (oldInst !== null) {
      out.newPaymentLine = `${periodTitle}: —`;
    }
  }

  if (oldTerm !== null && newTerm !== null) {
    const diff = newTerm - oldTerm;
    if (Math.abs(diff) >= 0.005) {
      const diffFormatted = diff >= 0 ? `+${formatPremiumDisplay(diff)}` : formatPremiumDisplay(diff);
      out.diffTermLine = `Difference (policy premium): ${diffFormatted}`;
    }
  }

  if (periodTitle && oldInst !== null && newInst !== null) {
    const diffI = newInst - oldInst;
    if (Math.abs(diffI) >= 0.005) {
      const diffFormatted = diffI >= 0 ? `+${formatPremiumDisplay(diffI)}` : formatPremiumDisplay(diffI);
      out.diffPaymentLine = `Difference (${periodTitle.toLowerCase()}): ${diffFormatted}`;
    }
  }

  return out;
}

export interface PremiumChangeDetails {
  unchanged: boolean;
  /** Whether the premium increased or decreased. */
  direction: 'increase' | 'decrease' | 'unchanged';
  /** The new premium to display — instalment for monthly/quarterly, term total for annual. */
  newAmount: number | null;
  /** Absolute difference between old and new display amounts. */
  difference: number | null;
  /** "monthly", "quarterly", or "annual". */
  frequencyLabel: string;
  /** "month", "quarter", or "year". */
  periodLabel: string;
  /**
   * True when instalment-based comparison was unavailable and amounts are term totals
   * while labels still reflect the policy payment frequency.
   */
  isFallback?: boolean;
}

/**
 * Structured premium change data for the redesigned confirmation screen.
 * Shows only the billing-frequency-relevant amount (monthly instalment OR annual term total).
 */
export function getPremiumChangeDetails(params: {
  oldTermPremium: number | null | undefined;
  newTermPremium: number | null | undefined;
  oldInstalment: number | null | undefined;
  paymentFrequencyRaw: string | undefined;
}): PremiumChangeDetails {
  const { oldTermPremium, newTermPremium, oldInstalment, paymentFrequencyRaw } = params;
  const freq = String(paymentFrequencyRaw ?? '').toUpperCase();

  const isMonthly = freq === 'MONTHLY';
  const isQuarterly = freq === 'QUARTERLY';
  const frequencyLabel = isMonthly ? 'monthly' : isQuarterly ? 'quarterly' : 'annual';
  const periodLabel = isMonthly ? 'month' : isQuarterly ? 'quarter' : 'year';

  const oldTerm = oldTermPremium != null && !Number.isNaN(oldTermPremium) ? oldTermPremium : null;
  const newTerm = newTermPremium != null && !Number.isNaN(newTermPremium) ? newTermPremium : null;

  const unchanged: PremiumChangeDetails = {
    unchanged: true, direction: 'unchanged', newAmount: null, difference: null, frequencyLabel, periodLabel,
  };

  if (oldTerm === null && newTerm === null) return unchanged;

  // For periodic billing, use instalment amounts
  if (isMonthly || isQuarterly) {
    const oldInst = oldInstalment != null && !Number.isNaN(oldInstalment) && oldInstalment >= 0 ? oldInstalment : null;
    const newInst = oldTerm != null && newTerm != null && oldInst != null
      ? estimateNewInstalmentAmount(oldTerm, newTerm, oldInst)
      : null;

    if (oldInst !== null && newInst !== null) {
      const diff = newInst - oldInst;
      if (Math.abs(diff) < 0.005) {
        return { ...unchanged, newAmount: newInst };
      }
      return {
        unchanged: false,
        direction: diff > 0 ? 'increase' : 'decrease',
        newAmount: newInst,
        difference: Math.abs(Math.round(diff * 100) / 100),
        frequencyLabel,
        periodLabel,
      };
    }
    // Fall through to term-based if instalment cannot be estimated
  }

  // Annual billing or fallback: use term premium
  if (oldTerm !== null && newTerm !== null) {
    const diff = newTerm - oldTerm;
    if (Math.abs(diff) < 0.005) {
      return { ...unchanged, newAmount: newTerm };
    }
    return {
      unchanged: false,
      direction: diff > 0 ? 'increase' : 'decrease',
      newAmount: newTerm,
      difference: Math.abs(Math.round(diff * 100) / 100),
      frequencyLabel,
      periodLabel,
      isFallback: true,
    };
  }

  // Only new term available (no old to compare)
  if (newTerm !== null) {
    return {
      unchanged: false,
      direction: 'unchanged',
      newAmount: newTerm,
      difference: null,
      frequencyLabel,
      periodLabel,
      isFallback: true,
    };
  }

  return unchanged;
}

/** @deprecated Use getPremiumComparisonLines — avoids labelling term total as monthly/annual instalment. */
export interface PremiumChangeDisplay {
  unchanged: boolean;
  oldLine?: string;
  newLine?: string;
  differenceLine?: string;
}

/** @deprecated Use getPremiumComparisonLines */
export function getPremiumChangeDisplay(params: {
  oldPremium: number | null | undefined;
  newPremium: number | null | undefined;
  frequencyLabel: string;
}): PremiumChangeDisplay {
  const { oldPremium, newPremium, frequencyLabel } = params;
  const oldVal = oldPremium != null && !Number.isNaN(oldPremium) ? oldPremium : null;
  const newVal = newPremium != null && !Number.isNaN(newPremium) ? newPremium : null;

  if (oldVal === null && newVal === null) {
    return { unchanged: true };
  }
  if (oldVal !== null && newVal !== null && Math.abs(oldVal - newVal) < 0.005) {
    return { unchanged: true };
  }
  if (oldVal === null && newVal !== null) {
    return { unchanged: false, newLine: `New premium: ${formatPremiumDisplay(newVal)} ${frequencyLabel}` };
  }
  if (oldVal !== null && newVal === null) {
    return { unchanged: false, newLine: `New premium: — ${frequencyLabel}` };
  }

  if (oldVal === null || newVal === null) return { unchanged: true };

  const diff = newVal - oldVal;
  const diffFormatted = diff >= 0 ? `+${formatPremiumDisplay(diff)}` : formatPremiumDisplay(diff);

  return {
    unchanged: false,
    oldLine: `Old premium: ${formatPremiumDisplay(oldVal)} ${frequencyLabel}`,
    newLine: `New premium: ${formatPremiumDisplay(newVal)} ${frequencyLabel}`,
    differenceLine: `Difference: ${diffFormatted} ${frequencyLabel}`,
  };
}

/**
 * Copy for the confirm-step line below the premium difference explanation.
 * Monthly policies include the upcoming payment date when available; annual/quarterly do not.
 */
export function nextPaymentStartCopy(
  frequencyLabel: string,
  nextInstalmentDueDateFormatted: string | null | undefined,
): string {
  const formatted = nextInstalmentDueDateFormatted?.trim();
  if (frequencyLabel === 'monthly' && formatted) {
    return `Your new premium will start from your next payment, on ${formatted}.`;
  }
  return 'Your new premium will start from your next payment.';
}
